import React, { useContext, useEffect } from 'react';

import { Fab, Grid, Paper, TextField, Zoom } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';

import { GlobalContext } from '../../../../store';

import Button from '../../../../components/Button';
import Modal from '../../../../components/Modal';
import YesNoSwitch from '../../../../components/YesNoSwitch';
import { toHandicap } from '../../../../contrib/Utils/toHandicap';
import { useBoolean } from '../../../common/Hooks/useBoolean.hook';

interface IProps {
  clubId: number;
  userId: number;
}

const BasicInformation = (props: IProps) => {
  const { clubId, userId } = props;

  const {
    actions: {
      adminMemberships: { updateByUser },
      notifications: { pushError, pushSuccess }
    },
    adminMemberships: memberships
  } = useContext(GlobalContext);

  const [editionEnabled, , toggleEdition] = useBoolean(false);
  const [isUserActive, setIsUserActive, toggleUserMembership] = useBoolean(false);
  const [modalIsOpen, , modalToggle] = useBoolean(false);

  const userMembership = memberships.memberships.find(
    membership => Number(membership.id) === Number(userId)
  );

  const update = async () => {
    const data = {
      status: isUserActive ? 'active' : 'inactive'
    };
    const response = await updateByUser(clubId, userId, data);

    if (response.errors) {
      pushError(response.errors.memberships.msg);
    } else {
      pushSuccess('Club membership updated successfully');
    }
  };

  useEffect(() => {
    if (userMembership) {
      setIsUserActive(userMembership.status === 'active');
    }
  }, [userMembership]);

  return (
    <div className="admin-layout">
      <div className="basic-information-wrapper admin-wrapper">
        <div className="main-container">
          <div className="top-row">
            {/* PLAYER INFORMATION */}
            {!!userMembership && (
              <Paper elevation={1} className="paper top-paper override">
                <div className="paper-header">
                  <h3>PLAYER INFORMATION</h3>
                </div>

                <Grid className="paper-content" container={true} spacing={8}>
                  <Grid className="row" container={true} item={true} spacing={8}>
                    <Grid className="row-title" item={true} xs={true}>
                      <p>EMAIL ADDRESS</p>
                    </Grid>
                    <Grid className="row-value" item={true} xs={true}>
                      <p>{userMembership.user.email}</p>
                    </Grid>
                  </Grid>

                  <Grid className="row" container={true} item={true} spacing={8}>
                    <Grid className="row-title" item={true} xs={true}>
                      <p>PHONE NUMBER</p>
                    </Grid>
                    <Grid className="row-value" item={true} xs={true}>
                      {/* REMOVE OR CHANGE THIS */}
                      <p>-</p>
                    </Grid>
                  </Grid>

                  <Grid className="row" container={true} item={true} spacing={8}>
                    <Grid className="row-title" item={true} xs={true}>
                      <p>BIRTH DATE</p>
                    </Grid>

                    <Grid className="row-value" item={true} xs={true}>
                      <p>
                        {userMembership.user.profile.birthDate === null
                          ? `-`
                          : userMembership.user.profile.birthDate}
                      </p>
                    </Grid>
                  </Grid>
                  <Grid className="row" container={true} item={true} spacing={8}>
                    <Grid className="row-title" item={true} xs={true}>
                      <p>GENDER</p>
                    </Grid>

                    <Grid className="row-value" item={true} xs={true}>
                      <p>{userMembership.user.profile.gender}</p>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            )}
            {/* HANDICAP */}
            {!!userMembership && (
              <Paper elevation={1} className="paper top-paper override">
                <div className="handicap-title paper-header">
                  <h3>PLAYER HANDICAP</h3>
                  <p>LAST UPDATED: DEC 31, 2019 </p>
                </div>
                <div className="paper-content">
                  <div className="stats-container">
                    {/* CURRENT INDEX */}
                    <div className="stats-values">
                      <div className="stats-values__wrap">
                        <p>CURRENT INDEX</p>
                        {/* Use the component <Input type="handicap" /> if this field must be edited */}
                        <TextField
                          id="outlined-bare"
                          className="stats-text-field"
                          InputProps={{
                            classes: {
                              input: 'stats-text-field__input override'
                            }
                          }}
                          defaultValue={toHandicap(userMembership.user.profile.handicapCurrent)}
                          margin="normal"
                          disabled={true}
                          variant="outlined"
                        />
                      </div>
                    </div>
                    {/* LOW INDEX */}
                    <div className="stats-values">
                      <div className="stats-values__wrap">
                        <p>LOW INDEX</p>
                        {/* Use the component <Input type="handicap" /> if this field must be edited */}
                        <TextField
                          id="outlined-bare"
                          className="stats-text-field"
                          InputProps={{
                            classes: {
                              input: 'stats-text-field__input override'
                            }
                          }}
                          defaultValue={toHandicap(userMembership.user.profile.handicapLow)}
                          margin="normal"
                          disabled={true}
                          variant="outlined"
                        />
                      </div>
                    </div>
                    {/* CLUB INDEX */}
                    <div className="stats-values">
                      <div className="stats-values__wrap">
                        <p>CLUB INDEX</p>
                        {/* Use the component <Input type="handicap" /> if this field must be edited */}
                        <TextField
                          id="outlined-bare"
                          className="stats-text-field"
                          InputProps={{
                            classes: {
                              input: 'stats-text-field__input override'
                            }
                          }}
                          defaultValue="-"
                          margin="normal"
                          disabled={true}
                          variant="outlined"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Paper>
            )}
          </div>
          <div className="bottom-row">
            {/* MEMBERSHIPS */}
            {!!userMembership && (
              <Paper elevation={1} className="paper bottom-paper override">
                <div className="membership-title paper-header">
                  <h3>MEMBERSHIP</h3>
                  <div className="buttons-toggle-zoom">
                    <Zoom in={!editionEnabled}>
                      <Fab
                        color="secondary"
                        onClick={toggleEdition}
                        size="small"
                        aria-label="Edit"
                        className="fab-edit"
                      >
                        <EditIcon className="edit-card-icon" />
                      </Fab>
                    </Zoom>
                    <Zoom in={editionEnabled}>
                      <div hidden={!editionEnabled} className="membership-edit-buttons">
                        <Button
                          onClick={update}
                          variant="contained"
                          color="primary"
                          className="button save-settings-button"
                        >
                          SAVE
                        </Button>
                        <Button
                          onClick={toggleEdition}
                          variant="text"
                          color="primary"
                          className="button cancel-settings-edition"
                        >
                          CANCEL
                        </Button>
                      </div>
                    </Zoom>
                  </div>
                </div>
                <Grid className="paper-content" container={true} spacing={8}>
                  <Grid className="row" container={true} item={true} spacing={8}>
                    <Grid className="row-title" item={true} xs={true}>
                      <p>MEMBER SINCE</p>
                    </Grid>
                    <Grid className="row-value" item={true} xs={true}>
                      {/* REMOVE OR CHANGE THIS */}
                      <p>-</p>
                    </Grid>
                  </Grid>
                  <Grid className="row" container={true} item={true} spacing={8}>
                    <Grid className="row-title" item={true} xs={true}>
                      <p>DATE OF RENEWAL</p>
                    </Grid>
                    <Grid className="row-value" item={true} xs={true}>
                      {/* REMOVE OR CHANGE THIS */}
                      <p>-</p>
                    </Grid>
                  </Grid>
                  <Grid className="row" container={true} item={true} spacing={8}>
                    <Grid className="row-title" item={true} xs={true}>
                      <p>MEMBERSHIP STATUS</p>
                    </Grid>
                    <Grid className="row-value" item={true} xs={true}>
                      <div className="membership-switch">
                        <YesNoSwitch
                          checked={isUserActive}
                          disabled={!editionEnabled}
                          onChange={toggleUserMembership}
                          labelNo="NOT ACTIVE"
                          labelYes="ACTIVE"
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <Grid className="row" container={true} item={true} spacing={8}>
                    <Grid className="row-title" item={true} xs={true}>
                      <p>REMOVE MEMBER</p>
                    </Grid>
                    <Grid className="row-value" item={true} xs={true}>
                      <Button
                        onClick={modalToggle}
                        disabled={!editionEnabled}
                        variant="contained"
                        color="primary"
                        className="button membership-renewal-button"
                      >
                        CONFIRM REMOVAL
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* CLUB STATS */}
            {!!userMembership && (
              <Paper elevation={1} className="paper bottom-paper override">
                <div className="paper-header">
                  <h3>CLUB STATS</h3>
                </div>
                <div className="paper-content">
                  <div className="stats-container">
                    {/* TOTAL EVENTS */}
                    <div className="stats-values">
                      <div className="stats-values__wrap">
                        <p>TOTAL EVENTS</p>
                        {/* Use the component <Input type="handicap" /> if this field must be edited */}
                        <TextField
                          id="outlined-bare"
                          className="stats-text-field"
                          InputProps={{
                            classes: {
                              input: 'stats-text-field__input override'
                            }
                          }}
                          defaultValue="-"
                          margin="normal"
                          disabled={true}
                          variant="outlined"
                        />
                      </div>
                    </div>
                    {/* AVG. GROSS SCORE */}
                    <div className="stats-values">
                      <div className="stats-values__wrap">
                        <p>AVG. GROSS SCORE</p>
                        {/* Use the component <Input type="handicap" /> if this field must be edited */}
                        <TextField
                          id="outlined-bare"
                          className="stats-text-field"
                          InputProps={{
                            classes: {
                              input: 'stats-text-field__input override'
                            }
                          }}
                          defaultValue="-"
                          margin="normal"
                          disabled={true}
                          variant="outlined"
                        />
                      </div>
                    </div>
                    {/* AVG. NET SCORE */}
                    <div className="stats-values">
                      <div className="stats-values__wrap">
                        <p>AVG. NET SCORE</p>
                        {/* Use the component <Input type="handicap" /> if this field must be edited */}
                        <TextField
                          id="outlined-bare"
                          className="stats-text-field"
                          InputProps={{
                            classes: {
                              input: 'stats-text-field__input override'
                            }
                          }}
                          defaultValue="-"
                          margin="normal"
                          disabled={true}
                          variant="outlined"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Paper>
            )}
          </div>
        </div>

        <Modal
          onClose={modalToggle}
          className="staff-scope"
          open={modalIsOpen}
          title="DELETE PLAYER"
          actions={[
            <Button
              onClick={modalToggle}
              variant="contained"
              className="confirm-removal-button"
              color="primary"
              key="button-yes"
            >
              YES, REMOVE THIS PLAYER FROM THE CLUB
            </Button>
          ]}
          wideButton={true}
        >
          <p>
            By removing this player from your club they will no longer receive any future
            information about the club or events as well as being removed from all player roster
            groups. Their historical finishes in events and standings will be archived for
            reference.
            <br />
            <br />
            If this player would like to be reinstated after being removed he or she will need to be
            invited again the same way a brand new player would be added to the club.
          </p>
        </Modal>
      </div>
    </div>
  );
};

export default BasicInformation;
